"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const wensityCardSurfaceClass = cn(
  "relative isolate overflow-hidden rounded-2xl",
  "border border-black/[0.08] bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,#ffffff_0%,#f4f4f5_72%)] [box-shadow:0_1px_2px_rgba(0,0,0,.06),0_8px_24px_-12px_rgba(0,0,0,.08)]",
  "dark:border-white/[0.06] dark:bg-[radial-gradient(ellipse_90%_70%_at_50%_-10%,#15151b_0%,#0a0a0b_72%)] dark:[box-shadow:0_1px_2px_rgba(0,0,0,.4),0_8px_24px_-12px_rgba(0,0,0,.6)]",
);

export interface BentoGridItem {
  id: string;
  colSpan?: 1 | 2 | 3 | 4;
  rowSpan?: 1 | 2 | 3;
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export interface BentoGridProps {
  items: BentoGridItem[];
  columns?: 3 | 4 | 5 | 6;
  gap?: number;
  /** Glow color as RGB string e.g. "205,28,24". Default is wensity red. */
  glowColor?: string;
  /** Proximity radius in px — how far the glow reaches. Default 200. */
  glowRadius?: number;
  /** Card shell — `double-frame` matches homepage marketing CTA cards. */
  cellVariant?: "default" | "double-frame";
  /** Top-center hairline + glow on `double-frame` cells. Default true. */
  doubleFrameTopAccent?: boolean;
  className?: string;
}

const COL: Record<number, string> = {
  1: "md:col-span-1", 2: "md:col-span-2", 3: "md:col-span-3", 4: "md:col-span-4",
};
const ROW: Record<number, string> = {
  1: "md:row-span-1", 2: "md:row-span-2", 3: "md:row-span-3",
};
const COLS: Record<number, string> = {
  3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5", 6: "md:grid-cols-6",
};

const DOUBLE_FRAME_OUTER =
  "rounded-[1.35rem] border border-[var(--border)] bg-[var(--surface-muted)] p-[3px] " +
  "[box-shadow:0_0_0_1px_rgba(0,0,0,.03),0_2px_4px_rgba(0,0,0,.05),0_12px_24px_rgba(0,0,0,.05)] " +
  "dark:border-white/[0.075] dark:bg-[#030304] " +
  "dark:[box-shadow:0_0_0_1px_rgba(255,255,255,.06),0_1px_2px_rgba(0,0,0,.6),0_24px_48px_-24px_rgba(0,0,0,.7)]";

const DOUBLE_FRAME_INNER = cn(
  wensityCardSurfaceClass,
  "h-full rounded-[1.12rem]",
);

const DOUBLE_FRAME_TOP_GLOW =
  "pointer-events-none absolute left-1/2 top-0 z-[1] h-8 w-56 -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,.16),transparent_72%)] blur-md dark:bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,.08),transparent_72%)]";

const DOUBLE_FRAME_HAIRLINE =
  "pointer-events-none absolute inset-x-0 top-0 z-[2] h-px bg-[linear-gradient(90deg,transparent,var(--border-strong),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.22),transparent)]";

const DOUBLE_FRAME_CENTER_HIGHLIGHT =
  "pointer-events-none absolute left-1/2 top-0 z-[3] h-px w-40 -translate-x-1/2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent)] dark:bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.35),transparent)]";

function DoubleFrameTopAccent() {
  return (
    <>
      <div aria-hidden className={DOUBLE_FRAME_TOP_GLOW} />
      <div aria-hidden className={DOUBLE_FRAME_HAIRLINE} />
      <div aria-hidden className={DOUBLE_FRAME_CENTER_HIGHLIGHT} />
    </>
  );
}

export function BentoGrid({
  items,
  columns = 4,
  gap = 16,
  glowColor = "205,28,24",
  glowRadius = 200,
  cellVariant = "default",
  doubleFrameTopAccent = true,
  className,
}: BentoGridProps) {
  const gridRef = React.useRef<HTMLDivElement>(null);
  const rafRef = React.useRef<number>(0);
  const isDoubleFrame = cellVariant === "double-frame";

  const handleMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDoubleFrame) return;

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const cards = gridRef.current?.querySelectorAll<HTMLElement>("[data-bento-card]");
      if (!cards) return;

      const proximity = glowRadius * 0.5;
      const fadeDistance = glowRadius * 0.85;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const relY = e.clientY - rect.top;

        // Nearest point on card to cursor
        const clampedX = Math.max(0, Math.min(rect.width, relX));
        const clampedY = Math.max(0, Math.min(rect.height, relY));
        const dx = relX - clampedX;
        const dy = relY - clampedY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        let intensity = 0;
        if (dist <= proximity) intensity = 1;
        else if (dist <= fadeDistance) intensity = 1 - (dist - proximity) / (fadeDistance - proximity);

        card.style.setProperty("--glow-x", `${relX}px`);
        card.style.setProperty("--glow-y", `${relY}px`);
        card.style.setProperty("--glow-intensity", intensity.toFixed(3));
      });
    });
  }, [glowRadius, isDoubleFrame]);

  const handleMouseLeave = React.useCallback(() => {
    if (isDoubleFrame) return;

    cancelAnimationFrame(rafRef.current);
    const cards = gridRef.current?.querySelectorAll<HTMLElement>("[data-bento-card]");
    cards?.forEach((card) => card.style.setProperty("--glow-intensity", "0"));
  }, [isDoubleFrame]);

  return (
    <>
      <style>{`
        [data-bento-grid] [data-bento-card] {
          --glow-x: 50%;
          --glow-y: 50%;
          --glow-intensity: 0;
        }
        [data-bento-grid][data-bento-variant="default"] [data-bento-card]::after {
          content: '';
          position: absolute;
          inset: 0;
          padding: 1.5px;
          border-radius: inherit;
          background: radial-gradient(
            ${glowRadius}px circle at var(--glow-x) var(--glow-y),
            rgba(205,28,24, calc(var(--glow-intensity) * 0.95)) 0%,
            rgba(255,138,115, calc(var(--glow-intensity) * 0.7)) 30%,
            rgba(255,200,150, calc(var(--glow-intensity) * 0.3)) 55%,
            transparent 70%
          );
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          pointer-events: none;
          z-index: 2;
          transition: opacity 0.15s ease;
        }
        [data-bento-grid][data-bento-variant="default"] [data-bento-card]::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: radial-gradient(
            ${glowRadius * 0.7}px circle at var(--glow-x) var(--glow-y),
            rgba(${glowColor}, calc(var(--glow-intensity) * 0.07)) 0%,
            transparent 70%
          );
          pointer-events: none;
          z-index: 1;
        }
      `}</style>

      <motion.div
        ref={gridRef}
        data-bento-grid
        data-bento-variant={cellVariant}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
        className={cn("grid w-full grid-cols-1 sm:grid-cols-2", COLS[columns], className)}
        style={{ gap }}
      >
        {items.map((item) => (
          <BentoCell
            key={item.id}
            item={item}
            cellVariant={cellVariant}
            doubleFrameTopAccent={doubleFrameTopAccent}
          />
        ))}
      </motion.div>
    </>
  );
}

function BentoCell({
  item,
  cellVariant,
  doubleFrameTopAccent,
}: {
  item: BentoGridItem;
  cellVariant: "default" | "double-frame";
  doubleFrameTopAccent: boolean;
}) {
  const { colSpan = 1, rowSpan = 1, title, description, icon, children, className } = item;
  const content = (
    <div className="relative z-10 flex h-full flex-col">
      {children ?? (
        <>
          {icon && (
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "rgba(205,28,24,0.08)", border: "1px solid rgba(205,28,24,0.15)", color: "#cd1c18" }}>
              {icon}
            </div>
          )}
          {title && <h3 className="font-display text-[17px] font-bold leading-tight tracking-tight text-black dark:text-white">{title}</h3>}
          {description && <p className="mt-2 text-[13.5px] leading-relaxed text-black/50 dark:text-white/50">{description}</p>}
        </>
      )}
    </div>
  );

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 14 },
        show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 280, damping: 24 } },
      }}
      className={cn(COL[colSpan], ROW[rowSpan])}
    >
      {cellVariant === "double-frame" ? (
        <div className={cn("h-full", DOUBLE_FRAME_OUTER, className)}>
          <div className={cn(DOUBLE_FRAME_INNER, "p-6")}>
            {doubleFrameTopAccent ? <DoubleFrameTopAccent /> : null}
            {content}
          </div>
        </div>
      ) : (
        <div
          data-bento-card
          className={cn(
            wensityCardSurfaceClass,
            "h-full p-6",
            className,
          )}
        >
          {content}
        </div>
      )}
    </motion.div>
  );
}
